import { z } from "zod";
import type { CategoryReadiness } from "./progress";
import { sessionSchema } from "./study";

/**
 * "Today's session" (plan §5.2, BE7): one button on Today that mixes the cards due now with practice questions
 * from all of the student's sets, weighted toward their weakest blueprint categories. Sized from the daily goal.
 */
export const TODAY_RULES = {
  /** Rough minutes per due card and per question, for sizing against the daily goal. */
  seconds_per_card: 12,
  seconds_per_question: 90,
  min_questions: 5,
  max_questions: 20,
  /** Due cards shown in one sitting; the rest stay due for the next run. */
  max_cards: 60,
  /** Categories with no data yet count as this weak, so new areas get practice too. */
  unknown_strength: 0.5,
} as const;

export interface TodayPoolItem {
  id: string;
  category: string | undefined;
  /** Answered in the last 7 days: used only when nothing fresher is left. */
  recent: boolean;
}

/** How many cards and questions fit the daily goal. */
export function todaySize(goalMinutes: number, dueCards: number): { cards: number; questions: number } {
  const cards = Math.min(dueCards, TODAY_RULES.max_cards);
  const left = goalMinutes * 60 - cards * TODAY_RULES.seconds_per_card;
  const questions = Math.max(TODAY_RULES.min_questions, Math.min(TODAY_RULES.max_questions, Math.floor(left / TODAY_RULES.seconds_per_question)));
  return { cards, questions };
}

/**
 * Picks `count` questions: each category gets a share proportional to blueprint weight × weakness (1 − strength),
 * filled with fresh questions first; spare slots go to whatever is left. Deterministic given `shuffle`.
 */
export function pickTodayQuestions(
  pool: TodayPoolItem[],
  categories: Pick<CategoryReadiness, "key" | "weight" | "value">[],
  count: number,
  shuffle: <T>(items: T[]) => T[] = (items) => items,
): string[] {
  const byCategory = new Map<string, TodayPoolItem[]>();
  for (const q of pool) if (q.category) byCategory.set(q.category, [...(byCategory.get(q.category) ?? []), q]);
  const ordered = (items: TodayPoolItem[]) => [...shuffle(items.filter((q) => !q.recent)), ...shuffle(items.filter((q) => q.recent))];

  const need = categories
    .filter((c) => byCategory.has(c.key))
    .map((c) => ({ key: c.key, score: c.weight * (1 - (c.value ?? TODAY_RULES.unknown_strength)) + 0.01 }));
  const total = need.reduce((n, c) => n + c.score, 0);
  const picked: string[] = [];
  const quota = need
    .map((c) => ({ ...c, exact: total > 0 ? (c.score / total) * count : 0 }))
    .map((c) => ({ ...c, n: Math.floor(c.exact) }));
  // Largest remainder for the slots left after rounding down.
  let spare = count - quota.reduce((n, c) => n + c.n, 0);
  for (const c of [...quota].sort((a, b) => b.exact - b.n - (a.exact - a.n))) {
    if (spare <= 0) break;
    c.n += 1;
    spare -= 1;
  }
  // Quotas take fresh questions only; then fresh ones from any category; recently seen ones only as a last resort.
  for (const c of quota) picked.push(...shuffle((byCategory.get(c.key) ?? []).filter((q) => !q.recent)).slice(0, c.n).map((q) => q.id));
  const taken = new Set(picked);
  for (const q of ordered(pool.filter((q) => !taken.has(q.id)))) {
    if (picked.length >= count) break;
    picked.push(q.id);
  }
  return picked;
}

/** GET /today: what the Today button will do. */
export const todayPlanSchema = z.object({
  due_cards: z.number().int().nonnegative(),
  cards: z.number().int().nonnegative(),
  questions_available: z.number().int().nonnegative(),
  questions: z.number().int().nonnegative(),
  minutes: z.number().int().nonnegative(),
  /** The weakest categories this session leans toward (at most two). */
  focus: z.array(z.object({ key: z.string(), name: z.string() })),
});
export type TodayPlan = z.infer<typeof todayPlanSchema>;

/** POST /sessions/today: the question part as a normal tutor-mode session (null when there are no questions yet). */
export const todaySessionResponseSchema = z.object({ plan: todayPlanSchema, session: sessionSchema.nullable() });
export type TodaySessionResponse = z.infer<typeof todaySessionResponseSchema>;
